/* eslint-disable */
import { useEffect, useState, useCallback } from "react";
import {
  ArrowLeft,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Check,
  X,
} from "lucide-react";
import { docsApi, categoryMeta, fetchBlobWithAuth, formatDate, formatFileSize } from "./api";

const REASONS = [
  "Inappropriate content",
  "Against community guidelines",
  "Incomplete/unclear",
  "Spam",
  "Other",
];

export default function ApprovalQueue({ onBack, onChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await docsApi.list({ pending_only: true, limit: 200 });
      setDocs(data.documents || []);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id) => {
    setBusyId(id);
    setError("");
    try {
      await docsApi.approve(id);
      await load();
      onChange && onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    if (!reason) {
      setError("Please select a reason");
      return;
    }
    setBusyId(rejectTarget.id);
    setError("");
    try {
      await docsApi.reject(rejectTarget.id, reason, feedback);
      setRejectTarget(null);
      setReason("");
      setFeedback("");
      await load();
      onChange && onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const oldest = docs.reduce((m, d) => Math.max(m, d.days_waiting || 0), 0);

  const handleView = async (d) => {
    try {
      const blob = await fetchBlobWithAuth(docsApi.previewUrl(d.id));
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // The blob URL stays alive as long as the new tab references it.
      // Revoke after a short delay so we don't leak forever.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError("Could not load the document preview.");
    }
  };

  return (
    <div className="docs-wrap">
      <div className="docs-toolbar">
        <div className="docs-approvals-head">
          <div>
            <button className="docs-back-btn" onClick={onBack} aria-label="Back">
              <ArrowLeft size={18} />
            </button>
            <h1>Pending Approvals</h1>
          </div>
          <span className="docs-pending-large">{docs.length}</span>
        </div>

        <div className="docs-stats">
          <div className="docs-stat">
            <p>Total Pending</p>
            <strong>{docs.length}</strong>
          </div>
          <div className="docs-stat">
            <p>Oldest Waiting</p>
            <strong>{oldest} day{oldest === 1 ? "" : "s"}</strong>
          </div>
          <div className="docs-stat">
            <p>Status</p>
            <strong>{docs.length === 0 ? "All caught up" : "Action needed"}</strong>
          </div>
        </div>
      </div>

      <div className="docs-content">
        {error && <div className="docs-error">{error}</div>}
        {loading ? (
          <div className="docs-empty">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="docs-empty docs-empty-success">
            <div className="docs-empty-icon">
              <CheckCircle2 size={32} strokeWidth={1.6} />
            </div>
            <h3>All caught up!</h3>
            <p>No pending documents to review</p>
          </div>
        ) : (
          <div className="docs-list">
            {docs.map((d) => {
              const meta = categoryMeta(d.category);
              return (
                <div key={d.id} className="docs-approval-card">
                  <div className="docs-approval-body">
                    <h3>{d.title}</h3>
                    {d.description && <p className="docs-approval-desc">{d.description}</p>}
                    <div className="docs-approval-meta">
                      <span>Uploaded by <strong>{d.owner}</strong></span>
                      <span>•</span>
                      <span>
                        <span className="docs-cat-pill" style={{ background: meta.color }}>
                          {meta.Icon && <meta.Icon size={11} strokeWidth={1.8} style={{ verticalAlign: "-1px", marginRight: 3 }} />}
                          {meta.label}
                        </span>
                      </span>
                      <span>•</span>
                      <span>{formatDate(d.uploaded_at)}</span>
                      <span>•</span>
                      <span>{d.file_type} {formatFileSize(d.file_size)}</span>
                    </div>
                    {d.tags && d.tags.length > 0 && (
                      <div className="docs-card-tags">
                        {d.tags.map((t) => (
                          <span key={t} className="docs-card-tag">{t}</span>
                        ))}
                      </div>
                    )}
                    <p
                      className={`docs-waiting ${
                        (d.days_waiting || 0) > 3 ? "warn" : ""
                      }`}
                    >
                      {(d.days_waiting || 0) > 3 ? (
                        <AlertTriangle size={13} />
                      ) : (
                        <Clock size={13} />
                      )}{" "}
                      Waiting {d.days_waiting || 0} day
                      {d.days_waiting === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="docs-approval-actions">
                    <button
                      type="button"
                      className="docs-btn-view"
                      onClick={() => handleView(d)}
                    >
                      View
                    </button>
                    <button
                      className="docs-btn-approve"
                      disabled={busyId === d.id}
                      onClick={() => approve(d.id)}
                    >
                      <Check size={14} /> Approve
                    </button>
                    <button
                      className="docs-btn-reject"
                      disabled={busyId === d.id}
                      onClick={() => {
                        setRejectTarget(d);
                        setReason("");
                        setFeedback("");
                      }}
                    >
                      <X size={14} /> Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {rejectTarget && (
        <div className="docs-modal-overlay" onClick={() => setRejectTarget(null)}>
          <div
            className="docs-modal docs-modal-small"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="docs-modal-header">
              <h2>Reject Document</h2>
              <button
                className="docs-modal-close"
                onClick={() => setRejectTarget(null)}
              >
                ×
              </button>
            </div>
            <div className="docs-modal-body">
              <p className="docs-reject-target">
                "{rejectTarget.title}" by {rejectTarget.owner}
              </p>
              <div className="docs-field">
                <label>Reason for rejection <span className="req">*</span></label>
                <select value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option value="">Select a reason…</option>
                  {REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="docs-field">
                <label>Additional feedback (optional)</label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value.slice(0, 200))}
                  rows={3}
                  placeholder="Provide details to help the owner improve the document…"
                />
                <p className="docs-char-count">{feedback.length}/200</p>
              </div>
              {error && <div className="docs-error">{error}</div>}
            </div>
            <div className="docs-modal-actions">
              <button
                className="docs-btn-secondary"
                onClick={() => setRejectTarget(null)}
              >
                Cancel
              </button>
              <button
                className="docs-btn-reject"
                onClick={submitReject}
                disabled={!reason || busyId === rejectTarget.id}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
